import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Scissors,
  Calendar,
  Clock,
  IndianRupee,
  Store,
  ArrowRight,
  TrendingUp,
  RotateCw,
  CheckCircle2,
  Settings,
} from 'lucide-react';
import { useShopsByOwner, useOrdersByOwner } from '../../../api';
import styles from './dashboard.module.css';

const VendorDashboard = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [stats, setStats] = useState({
    totalServices: 0,
    totalBookings: 0,
    pendingBookings: 0,
    monthlyEarnings: 0,
  });
  const [recentBookings, setRecentBookings] = useState([]);

  // Get vendor's shops & orders
  const { data: shops = [], refetch: refetchShops } = useShopsByOwner(user?._id);
  const { data: orders = [], refetch: refetchOrders } = useOrdersByOwner(user?._id);

  const paidOrder = (order) =>
    order.isPaid === true ||
    String(order.paymentStatus || '').toUpperCase() === 'SUCCESS' ||
    String(order.paymentStatus || '').toLowerCase() === 'paid';

  // Calculate stats
  useEffect(() => {
    if (!shops || shops.length === 0) return;

    const totalServices = shops.reduce((sum, shop) => sum + (shop.products?.length || 0), 0);

    if (!orders || orders.length === 0) {
      setStats({
        totalServices,
        totalBookings: 0,
        pendingBookings: 0,
        monthlyEarnings: 0,
      });
      setRecentBookings([]);
      return;
    }

    const totalBookings = orders.length;
    const pendingBookings = orders.filter((order) => {
      const s = String(order.orderStatus || '').toLowerCase();
      return s === 'pending' || s === 'confirmed';
    }).length;

    const monthlyEarnings = orders
      .filter((order) => {
        const orderDate = new Date(order.createdAt);
        const currentDate = new Date();
        return (
          orderDate.getMonth() === currentDate.getMonth() &&
          orderDate.getFullYear() === currentDate.getFullYear() &&
          paidOrder(order)
        );
      })
      .reduce((sum, order) => sum + (order.grandTotal || 0), 0);

    setStats({
      totalServices,
      totalBookings,
      pendingBookings,
      monthlyEarnings,
    });

    setRecentBookings(orders.slice(0, 5));
  }, [shops, orders]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.allSettled([refetchShops(), refetchOrders()]);
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const getStatusBadge = (status) => {
    const s = String(status || '').toLowerCase();
    switch (s) {
      case 'pending':
        return <span className={`${styles.statusBadge} ${styles.pending}`}>Pending</span>;
      case 'confirmed':
        return <span className={`${styles.statusBadge} ${styles.confirmed}`}>Confirmed</span>;
      case 'completed':
        return <span className={`${styles.statusBadge} ${styles.completed}`}>Completed</span>;
      case 'cancelled':
        return <span className={`${styles.statusBadge} ${styles.cancelled}`}>Cancelled</span>;
      default:
        return <span className={`${styles.statusBadge} ${styles.pending}`}>{status}</span>;
    }
  };

  const todayFormatted = new Date().toLocaleDateString('en-IN', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  return (
    <div className={styles.dashboardContainer}>
      {/* Header Banner */}
      <div className={styles.pageHeader}>
        <div className={styles.welcomeText}>
          <h1 className={styles.pageTitle}>
            <span>Welcome, {user?.name || 'Partner'}</span>
            <span style={{ fontSize: '1.1rem' }}>👋</span>
          </h1>
          <p className={styles.pageSubtitle}>
            Monitor appointments, shop performance, and earnings across your salons
          </p>
        </div>

        <div className={styles.headerActions}>
          <div className={styles.datePill}>
            <Calendar size={13} />
            <span>{todayFormatted}</span>
          </div>
          <button
            className={styles.refreshButton}
            onClick={handleRefresh}
            title="Refresh dashboard metrics"
            disabled={isRefreshing}
          >
            <RotateCw size={13} className={isRefreshing ? styles.rotating : ''} />
            <span>Sync</span>
          </button>
        </div>
      </div>

      {/* Primary Key Stats */}
      <div className={styles.statsGrid}>
        {/* Total Services */}
        <div className={styles.statCard}>
          <div className={styles.statCardHeader}>
            <span className={styles.statLabel}>Active Services</span>
            <div className={`${styles.statIconBox} ${styles.crimson}`}>
              <Scissors size={16} />
            </div>
          </div>
          <div className={styles.statValue}>{stats.totalServices}</div>
          <div className={styles.statFooter}>
            <span className={`${styles.trendBadge} ${styles.neutral}`}>
              Across {shops.length} {shops.length === 1 ? 'Shop' : 'Shops'}
            </span>
          </div>
        </div>

        {/* Total Bookings */}
        <div className={styles.statCard}>
          <div className={styles.statCardHeader}>
            <span className={styles.statLabel}>Total Bookings</span>
            <div className={`${styles.statIconBox} ${styles.emerald}`}>
              <Calendar size={16} />
            </div>
          </div>
          <div className={styles.statValue}>{stats.totalBookings}</div>
          <div className={styles.statFooter}>
            <span className={`${styles.trendBadge} ${styles.up}`}>
              <TrendingUp size={11} /> All Time
            </span>
            <span>Customer appointments</span>
          </div>
        </div>

        {/* Pending Bookings */}
        <div className={styles.statCard}>
          <div className={styles.statCardHeader}>
            <span className={styles.statLabel}>Pending Attention</span>
            <div className={`${styles.statIconBox} ${styles.amber}`}>
              <Clock size={16} />
            </div>
          </div>
          <div className={styles.statValue}>{stats.pendingBookings}</div>
          <div className={styles.statFooter}>
            {stats.pendingBookings > 0 ? (
              <span className={`${styles.trendBadge} ${styles.alert}`}>Action required</span>
            ) : (
              <span className={`${styles.trendBadge} ${styles.up}`}>
                <CheckCircle2 size={11} /> All caught up
              </span>
            )}
          </div>
        </div>

        {/* Monthly Earnings */}
        <div className={styles.statCard}>
          <div className={styles.statCardHeader}>
            <span className={styles.statLabel}>Monthly Earnings</span>
            <div className={`${styles.statIconBox} ${styles.sky}`}>
              <IndianRupee size={16} />
            </div>
          </div>
          <div className={styles.statValue}>₹{stats.monthlyEarnings.toLocaleString()}</div>
          <div className={styles.statFooter}>
            <span className={`${styles.trendBadge} ${styles.up}`}>
              <TrendingUp size={11} /> This Month
            </span>
            <span>Settled orders</span>
          </div>
        </div>
      </div>

      {/* Your Shops Section */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Your Barbershops ({shops.length})</h2>
          <button className={styles.viewAllLink} onClick={() => navigate('/shops')}>
            <span>View All</span>
            <ArrowRight size={13} />
          </button>
        </div>

        {shops.length > 0 ? (
          <div className={styles.shopsGrid}>
            {shops.map((shop) => (
              <div key={shop._id} className={styles.shopCard}>
                <div className={styles.shopHeader}>
                  <div className={styles.shopName}>{shop.name}</div>
                  <span
                    className={`${styles.shopBadge} ${
                      shop.verified ? styles.verified : styles.pending
                    }`}
                  >
                    {shop.verified ? 'Verified' : 'Pending Verification'}
                  </span>
                </div>
                <p className={styles.shopDescription}>
                  {shop.description || 'Barbershop registered on B4BARBER platform.'}
                </p>
                <div className={styles.shopFooter}>
                  <span className={styles.shopServicesCount}>
                    {shop.products?.length || 0} Listed Services
                  </span>
                  <button
                    className={styles.btnManageShop}
                    onClick={() => navigate(`/shops/${shop._id}`)}
                  >
                    <span>Manage Catalog</span>
                    <ArrowRight size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.emptyCard}>
            <Store size={32} style={{ color: '#94a3b8', marginBottom: '8px' }} />
            <div className={styles.emptyTitle}>No Barbershops Registered</div>
            <p className={styles.emptyText}>
              Register your first salon location to begin offering services and accepting bookings.
            </p>
            <button
              className={styles.btnManageShop}
              style={{ marginTop: '12px' }}
              onClick={() => navigate('/shops')}
            >
              Add New Shop
            </button>
          </div>
        )}
      </div>

      {/* Recent Bookings Feed */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Recent Appointments</h2>
          <button className={styles.viewAllLink} onClick={() => navigate('/bookings')}>
            <span>View All Bookings</span>
            <ArrowRight size={13} />
          </button>
        </div>

        <div className={styles.bookingsCard}>
          {recentBookings.length > 0 ? (
            <div className={styles.tableWrapper}>
              <table className={styles.dataTable}>
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Date &amp; Time</th>
                    <th>Status</th>
                    <th>Amount</th>
                    <th style={{ textAlign: 'right' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {recentBookings.map((booking) => (
                    <tr key={booking._id}>
                      <td>
                        <div className={styles.customerMeta}>
                          <span className={styles.customerName}>
                            {booking.userDetails?.name || 'Customer'}
                          </span>
                          <span className={styles.orderNum}>
                            #{booking.orderNumber || String(booking._id).slice(-6)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span style={{ fontSize: '0.8rem', color: '#475569' }}>
                          {new Date(booking.createdAt).toLocaleDateString('en-IN', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </td>
                      <td>{getStatusBadge(booking.orderStatus)}</td>
                      <td>
                        <span className={styles.orderPrice}>
                          ₹{(booking.grandTotal || 0).toLocaleString()}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          className={styles.btnManageShop}
                          onClick={() => navigate('/bookings')}
                        >
                          Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={styles.emptyCard} style={{ border: 'none' }}>
              <Calendar size={32} style={{ color: '#94a3b8', marginBottom: '8px' }} />
              <div className={styles.emptyTitle}>No Recent Bookings</div>
              <p className={styles.emptyText}>
                New customer appointments will appear here in real-time.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Action Portals (Fixing previous broken navigation paths) */}
      <div>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Quick Management Portals</h2>
        </div>
        <div className={styles.actionsGrid}>
          <div className={styles.actionCard} onClick={() => navigate('/bookings')}>
            <div className={styles.actionIconBox}>
              <Calendar size={18} />
            </div>
            <div className={styles.actionContent}>
              <div className={styles.actionTitle}>Manage Bookings</div>
              <p className={styles.actionDesc}>
                View upcoming appointments, confirm slots, and update fulfillment
              </p>
            </div>
            <ArrowRight size={15} className={styles.actionArrow} />
          </div>

          <div className={styles.actionCard} onClick={() => navigate('/shops')}>
            <div className={styles.actionIconBox}>
              <Scissors size={18} />
            </div>
            <div className={styles.actionContent}>
              <div className={styles.actionTitle}>Shops &amp; Services Catalog</div>
              <p className={styles.actionDesc}>
                Configure service menus, pricing, duration, and shop profiles
              </p>
            </div>
            <ArrowRight size={15} className={styles.actionArrow} />
          </div>

          <div className={styles.actionCard} onClick={() => navigate('/settings')}>
            <div className={styles.actionIconBox}>
              <Settings size={18} />
            </div>
            <div className={styles.actionContent}>
              <div className={styles.actionTitle}>Profile &amp; Bank Settings</div>
              <p className={styles.actionDesc}>
                Update working hours, contact info, and bank payout credentials
              </p>
            </div>
            <ArrowRight size={15} className={styles.actionArrow} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorDashboard;
